import type { AxiosResponse } from 'axios'
import type {
  DestinationDetailList,
  QueryDestinationDetailListModel,
  PostSendRemindMail,
} from './model/sendMailModel'
import type { Params } from '@/store/modules/send-mail/state'
import type { QueryTransactionHistoryListModel } from '../transaction-history/model/transactionHistory'
import type { SendMailListCSVExportItem, SendMailListCSVExportParameter
} from './model/sendMailCSVModel'

import type { CodesDetail } from '@/api/model/basicTypes'
import type { CodesDetailItemsModel } from '@/api/model/codesModel'
import type { UseStorage } from '@/hooks/core/useStorage'
import type { BasicResult } from '@/api/model/resultModel'

import { bffHTTP } from '@/utils/http/axios'
import { handleAPIException, handleExceptionNormal } from '@/utils/exception'
import { ErrorModel } from '../model/errorModel'
import { ApiResponseStatusEnum, HTTPMethodsEnum } from '@/enums'
import { useStorage } from '@/hooks/core/useStorage'
import { CodesEnum } from '@/enums/codesEnum'
import { formatDate } from '@/utils'


/**
 * @description 取引履歴取得 API
 * @param params
 * @returns
 */
export async function queryTransactionList(
  params: Params
): Promise<QueryTransactionHistoryListModel | undefined> {
  try {
    const response: AxiosResponse<QueryTransactionHistoryListModel, any> = await bffHTTP({
      method: HTTPMethodsEnum.GET,
      url: '/transactions',
      params,
    })

    handleAPIException(response)
    return response.data
  } catch (error) {
    console.log('getCarts', error)
  }
}

/**
 * @description 取引履歴CSV取得 API
 * @param params
 * @returns
 */
export async function queryTransactionListCSV(
  params: Params
  ): Promise<SendMailListCSVExportParameter> {
  try {
      const response: AxiosResponse<BasicResult<SendMailListCSVExportItem>, null> = await bffHTTP({
      method: HTTPMethodsEnum.GET,
      url: '/transactions/List_CSV',
      params,
    })

    handleAPIException(response)
    return formatSendMailListTransferCsv([...response.data.detail])
  } catch (error) {
    console.log('queryTransactionListCSV', error)
    throw error
  }
}

/**
 * @description 商品一覧出力API結果フォーマットする
 * @description mapに設定した順番に出力
 * @param data 商品一覧出力API結果
 * @returns  商品一覧出力APIフォーマットされた結果
 */
function formatSendMailListTransferCsv(
  data: SendMailListCSVExportParameter
): SendMailListCSVExportParameter {

  const { loadCodesInformation }: UseStorage = useStorage()
  const codes = loadCodesInformation() as CodesDetail[]

  //ドキュメント種別(定義)を取得
  const documentTypeInfo = codes.find(
    (item: CodesDetail) => item.management_number === CodesEnum.DOCUMENT_TYPE
  ) as CodesDetail

  //取引ステータス(定義)を取得
  const transactionStatusInfo = codes.find(
    (item: CodesDetail) => item.management_number === CodesEnum.TRANSACTION_STATUS
  ) as CodesDetail

  //コード取得
  const findCodeName = (data: CodesDetail, code: string): string => {
    return (
      data.item.find((item: CodesDetailItemsModel) => code === item.value)?.name ?? ''
    )
  }

   return data.map<SendMailListCSVExportItem>((item) => ({

  sent_date: formatDate(item.sent_date),
  receive_date: formatDate(item.receive_date),
  transaction_status_status: findCodeName(transactionStatusInfo, item.transaction_status_status != '' ? item.transaction_status_status : item.transaction_status),
  sender_product_code: item.sender_product_code,
  sender_product_name: item.sender_product_name,
  document_type: findCodeName(documentTypeInfo, item.document_type),
  file_name: item.file_name,
  revision_date: formatDate(item.revision_date),
  recipient_corporate_number: item.recipient_corporate_number,
  recipient_client_code: item.recipient_client_code,
  recipient_corporate_name: item.recipient_corporate_name,
  recipient_department_name: item.recipient_department_name,
  recipient_user_name: item.recipient_user_name,
  recipient_folder_name: item.recipient_folder_name,
  sender_department_name: item.sender_department_name,
  sender_user_name: item.sender_user_name,
  recipient_message: item.recipient_message,
  sender_message: item.sender_message,

  transaction_status: '',

   }))
}

export async function getReceiveCount(): Promise<any | undefined> {
  try {
    const response: AxiosResponse<any, any> = await bffHTTP({
      method: HTTPMethodsEnum.GET,
      url: '/transactions/receive_count',
    })

    handleAPIException(response)
    return response
  } catch (error) {
    console.log('getCarts', error)
  }
}

export async function SendCount(): Promise<any | undefined> {
  try {
    const response: AxiosResponse<any, any> = await bffHTTP({
      method: HTTPMethodsEnum.GET,
      url: '/transactions/send_count',
    })

    handleAPIException(response)
    return response
  } catch (error) {
    console.log('getCarts', error)
  }
}

/**
 * @description 送信先詳細一覧取得 API
 * @param destinationID 送信先 ID
 */
export async function queryDestinationDetailList(
  destinationID: string | number
): Promise<DestinationDetailList> {
  try {
    const response: AxiosResponse<QueryDestinationDetailListModel, any> = await bffHTTP({
      method: HTTPMethodsEnum.GET,
      url: `/recipient_details/${destinationID.toString()}`,
    })

    handleAPIException(response)
    return response.data.detail
  } catch (error) {
    console.log('queryDestinationDetailList', error)
    return []
  }
}

/**
 * @descripiton 企業利用者情報削除 API
 * @param targetUserId 削除対象利用者 ID
 * @param notFoundCallback データ見つからない場合のコールバック
 * @returns 削除成功か否か
 */
export async function deleteGuestAccount(
  targetUserId: string | number | undefined,
  notFoundCallback: Function = () => {}
): Promise<boolean> {
  try {
    const response: AxiosResponse<string | ErrorModel, null> = await bffHTTP({
      method: HTTPMethodsEnum.DELETE,
      url: `/users/${targetUserId}`,
    })

    const failedResult = response.data as ErrorModel

    if (failedResult.error_code === 'EB0019') {
      notFoundCallback()
    }

    if (response.status !== ApiResponseStatusEnum.SUCCESSFUL_STATUS) {
      throw failedResult
    }

    return response.status === ApiResponseStatusEnum.SUCCESSFUL_STATUS
  } catch (error) {
    handleExceptionNormal(error)
    return false
  }
}

/**
 * @descripiton 取引ステータス別に取引履歴有無確認 API
 * @param transactionStatus 確認したい取引ステータス
 * @returns 該当取引ステータスのデータ存在か否か
 */
export async function getTransactionsExistsNotYetStatus(
  transactionStatus: string | undefined
): Promise<boolean | null> {
  try {
    const response: AxiosResponse<null, string | undefined> = await bffHTTP({
      method: HTTPMethodsEnum.GET,
      url: `/transactions/exists/${transactionStatus}`,
    })

    handleAPIException(response)

    return response.data
  } catch (error) {
    handleExceptionNormal(error)
    return null
  }
}

/**
 * @description リマインドメール送信一覧取得 API
 * @param params
 * @returns
 */
export async function queryRemindMailSendList(
  params: Params
): Promise<QueryTransactionHistoryListModel | undefined> {
  try {
    const response: AxiosResponse<QueryTransactionHistoryListModel, any> = await bffHTTP({
      method: HTTPMethodsEnum.GET,
      url: '/transactions/remind_mail',
      params,
    })
    handleAPIException(response)
    return response.data
  } catch (error) {
    console.log('queryRemindMailSendList', error)
  }
}

/**
 * @description リマインドメール送信 API
 * @param params
 * @returns
 */
export async function postRemindMailSend(
  params: PostSendRemindMail[],
): Promise<boolean> {
  try {
    const response: AxiosResponse<null, PostSendRemindMail> = await bffHTTP({
      method: HTTPMethodsEnum.POST,
      url: '/transactions/send_remind_mail',
      data: params,
    })

    handleAPIException(response)
    return true
  } catch (error) {
    console.log('postRemindMailSend', error)
    return false
  }
}